import type {
  CSSProperties,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactNode,
} from 'react';
import { useMemo } from 'react';
import { Link, NavLink } from 'react-router';

import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';
import { getUrlWithoutOrigin } from '#/utils/url/getUrlWithoutOrigin';
import { isUrlWithProtocol } from '#/utils/url/isUrlWithProtocol';

type Props = {
  children?: ReactNode;
  id?: string;
  className?: string;
  navLink?: boolean;
  to: string;
  state?: Record<string, unknown>;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tabIndex?: number;
  target?: HTMLAttributeAnchorTarget;
  title?: string;
  style?: CSSProperties;
  qaId?: string;
  preventScrollReset?: boolean;
  defaultShouldRevalidate?: boolean;
};

export function VersionedRouterLink({
  children,
  id,
  className,
  navLink,
  to,
  onClick,
  state,
  tabIndex,
  target,
  title,
  style,
  qaId,
  preventScrollReset,
  defaultShouldRevalidate,
  ...dataAndAriaProps
}: Props) {
  const normalizedTo = useMemo(
    () => (isUrlWithProtocol(to) ? getUrlWithoutOrigin(to) : to),
    [to],
  );

  const Component = navLink ? NavLink : Link;

  return (
    <Component
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...getDataAndAriaProps(dataAndAriaProps)}
      id={id}
      className={className}
      to={normalizedTo}
      onClick={onClick}
      state={state}
      discover="none"
      target={target}
      tabIndex={tabIndex}
      title={title}
      style={style}
      data-qa-id={qaId}
      preventScrollReset={preventScrollReset}
      defaultShouldRevalidate={defaultShouldRevalidate}
    >
      {children}
    </Component>
  );
}
